import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEvaluationModelBox, useTenderData } from 'src/models/procurements/Tender/hooks';
import { TenderSubCatName, TenderBlockSize, TenderBoxMode } from 'src/models/procurements/Tender/types';
import { isAddition } from 'src/models/procurements/Tender/helpers';
import { TenderBox, TenderSubCat } from 'src/shared';
import { Translations } from 'src/lib/I18nService/types';

export const ProcurementSubCat: FC = () => {
  const { t: tenderT } = useTranslation(Translations.procurements);
  const { data } = useTenderData();
  const isEditable = !!data?.isEditable;
  const subCat = data?.generalCat[TenderSubCatName.Procurement];
  const awardBox = useEvaluationModelBox();
  const questionBox = useMemo(
    () =>
      subCat?.boxes.deadlineOfClarificationQuestions && !!subCat.boxes.deadlineOfClarificationQuestions.length
        ? subCat.boxes.deadlineOfClarificationQuestions[0]
        : undefined,
    [subCat]
  );
  const deadlineBox = useMemo(
    () => (subCat?.boxes.deadline && !!subCat.boxes.deadline.length ? subCat.boxes.deadline[0] : undefined),
    [subCat]
  );
  const publishedBox = useMemo(
    () =>
      subCat?.boxes.availableDate && !!subCat.boxes.availableDate.length ? subCat.boxes.availableDate[0] : undefined,
    [subCat]
  );
  const procedureBox = useMemo(
    () =>
      subCat?.boxes.typeOfProcedureBoxes && !!subCat.boxes.typeOfProcedureBoxes.length
        ? subCat.boxes.typeOfProcedureBoxes[0]
        : undefined,
    [subCat]
  );
  const frameworkBox = useMemo(
    () =>
      subCat?.boxes.frameworkAgreement && !!subCat.boxes.frameworkAgreement.length
        ? subCat.boxes.frameworkAgreement[0]
        : undefined,
    [subCat]
  );
  const linkBox = useMemo(
    () =>
      subCat?.boxes.linkForSubmittingTenderBoxes && !!subCat.boxes.linkForSubmittingTenderBoxes.length
        ? subCat.boxes.linkForSubmittingTenderBoxes[0]
        : undefined,
    [subCat]
  );
  const refNumberBox = useMemo(
    () =>
      subCat?.boxes.referenceNumberBoxes && !!subCat.boxes.referenceNumberBoxes.length
        ? subCat.boxes.referenceNumberBoxes[0]
        : undefined,
    [subCat]
  );

  return (
    <>
      {subCat && (
        <TenderSubCat subCat={subCat}>
          <TenderBox
            box={questionBox}
            title={tenderT('Deadline of clarification questions')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.deadlineOfClarificationQuestions)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={deadlineBox}
            title={tenderT('Submission deadline')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.deadline)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={awardBox}
            title={tenderT('Evaluation model')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={false}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={publishedBox}
            title={tenderT('Publication date')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.availableDate)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={procedureBox}
            title={tenderT('Type of procedure')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.typeOfProcedureBoxes)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={frameworkBox}
            title={tenderT('Framework agreement')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.frameworkAgreement)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={linkBox}
            title={tenderT(linkBox?.title || 'Link for submitting tender')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.linkForSubmittingTenderBoxes)}
            isFirstField
            isEditable={isEditable}
          />
          <TenderBox
            box={refNumberBox}
            title={tenderT(refNumberBox?.title || 'Reference number')}
            as={TenderBoxMode.Custom}
            size={TenderBlockSize.Quarter}
            isInfo={isAddition(subCat?.boxes.referenceNumberBoxes)}
            isFirstField
            isEditable={isEditable}
          />
        </TenderSubCat>
      )}
    </>
  );
};

export default ProcurementSubCat;
